import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { accountApi } from '@/api/commercial/accounts';
import {
  type CreditAnalysis,
  CreditAnalysisStatus,
  creditAnalysisApi,
} from '@/api/commercial/credit-analysis';
import { proposalApi } from '@/api/commercial/proposals';
import { CreditAnalysisTimeline } from '@/components/commercial/credit-analysis/credit-analysis-timeline';
import { CreditAnalysisVerdictDetails } from '@/components/commercial/credit-analysis/credit-analysis-verdict-details';
import { NewAnalysisConfirmationDialog } from '@/components/dialogs/commercial/credit-analysis/new-analysis-confirmation-dialog';
import { DocumentUploadForm } from '@/components/forms/commercial/credit-analysis/document-upload-form';
import { ResendDocumentRequestDialog } from '@/components/forms/commercial/credit-analysis/resend-document-request-dialog';
import { SendDocumentRequestForm } from '@/components/forms/commercial/credit-analysis/send-document-request-form';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCreateProposal } from '@/context/create-proposal-context';
import { useCreditAnalysisExpiration } from '@/hooks/commercial';
import {
  normalizeAccountTypeForCreditAnalysis,
  useCreditAnalysisStateMachine,
} from '@/hooks/commercial/use-credit-analysis-state-machine';
import { useToast } from '@/hooks/use-toast';
import { useProposalNavigation } from '../shared/use-proposal-navigation';

const CREDIT_ANALYSIS_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  PENDING_EXTERNAL_DOCS: 'PENDING_EXTERNAL_DOCS',
  PENDING_INTERNAL_ANALYSIS: 'PENDING_INTERNAL_ANALYSIS',
  DOC_DECLINED: 'DOC_DECLINED',
  APPROVED: 'APPROVED',
  PARTIALLY_APPROVED: 'PARTIALLY_APPROVED',
  DECLINED: 'DECLINED',
  EXPIRED: 'EXPIRED',
} as const;

export function StepCreditAnalysis() {
  const _navigate = useNavigate();
  const _location = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigation = useProposalNavigation({ mode: 'create' });
  const {
    account,
    contacts,
    selectedContactIds,
    proposal,
    creditAnalysis,
    setCreditAnalysis,
    setProposal,
  } = useCreateProposal();

  const [localCreditAnalysis, setLocalCreditAnalysis] = useState<CreditAnalysis | null>(
    creditAnalysis,
  );
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [showNewAnalysisDialog, setShowNewAnalysisDialog] = useState(false);
  const [isCreatingAnalysis, setIsCreatingAnalysis] = useState(false);

  const status = localCreditAnalysis?.status ?? CREDIT_ANALYSIS_STATUS.NOT_STARTED;
  const hasCreditAnalysis = !!localCreditAnalysis;
  const accountType = normalizeAccountTypeForCreditAnalysis(
    account?.accountType,
    account?.simeiSince,
    account?.document?.type,
  );

  // V-FE-01: Binary expiration state UI
  const expirationValidation = useCreditAnalysisExpiration(localCreditAnalysis, proposal?.status);

  const stateMachine = useCreditAnalysisStateMachine({
    creditAnalysis: localCreditAnalysis,
    accountType,
  });
  const declinedDocuments = stateMachine.declinedDocuments;

  const filteredContacts = contacts.filter((contact) =>
    contact.contactItems?.some((item: { type: string }) => item.type === 'Email'),
  );

  const { data: accountDocuments } = useQuery({
    queryKey: ['accountDocuments', account?.id],
    queryFn: async () => {
      if (!account?.id) return null;
      return accountApi.findDocuments(account.id);
    },
    enabled: !!account?.id,
  });

  const allEmails: string[] = [
    ...(accountDocuments?.subjects?.map((s) => s.value) || []),
    ...(accountDocuments?.externalSubjects?.map((e) => e.value) || []),
  ];

  const { data: creditAnalysisByAccount } = useQuery({
    queryKey: ['creditAnalysis', 'account', account?.id],
    queryFn: async () => {
      if (!account?.id || !proposal?.id) return null;
      try {
        // Prefer the analysis from the proposal, fallback to account
        if (proposal.creditAnalysisId) {
          return await creditAnalysisApi.findOne(proposal.creditAnalysisId);
        }
        const result = await creditAnalysisApi.findByAccount(account.id);
        return result;
      } catch {
        return null;
      }
    },
    enabled: !!account?.id && !!proposal?.id,
  });

  const { data: fetchedCreditAnalysis, isLoading } = useQuery({
    queryKey: ['creditAnalysis', 'proposal', proposal?.id],
    queryFn: async () => {
      if (!proposal?.id) return null;
      try {
        const result = await creditAnalysisApi.findByProposal(proposal.id);
        return result;
      } catch {
        return null;
      }
    },
    enabled: !!proposal?.id,
  });

  const { data: timelineEntries } = useQuery({
    queryKey: ['creditAnalysisTimeline', localCreditAnalysis?.id],
    queryFn: async () => {
      if (!localCreditAnalysis?.id) return [];
      try {
        const data = await creditAnalysisApi.getTimeline(localCreditAnalysis.id);
        return data;
      } catch {
        return [];
      }
    },
    enabled: !!localCreditAnalysis?.id,
  });

  useEffect(() => {
    console.log('[CREDIT ANALYSIS DEBUG] === PROPOSAL CHANGE ===');
    console.log('[CREDIT ANALYSIS DEBUG] Proposal ID:', proposal?.id);
    console.log('[CREDIT ANALYSIS DEBUG] Proposal creditAnalysisId:', proposal?.creditAnalysisId);
    console.log('[CREDIT ANALYSIS DEBUG] fetchedCreditAnalysis:', {
      id: fetchedCreditAnalysis?.id,
      status: fetchedCreditAnalysis?.status,
      proposalId: fetchedCreditAnalysis?.proposalId,
      newAnalysisIn: fetchedCreditAnalysis?.newAnalysisIn,
    });
    console.log('[CREDIT ANALYSIS DEBUG] creditAnalysisByAccount:', {
      id: creditAnalysisByAccount?.id,
      status: creditAnalysisByAccount?.status,
      proposalId: creditAnalysisByAccount?.proposalId,
      newAnalysisIn: creditAnalysisByAccount?.newAnalysisIn,
    });
    console.log('[CREDIT ANALYSIS DEBUG] Context creditAnalysis:', {
      id: creditAnalysis?.id,
      status: creditAnalysis?.status,
      proposalId: creditAnalysis?.proposalId,
      newAnalysisIn: creditAnalysis?.newAnalysisIn,
    });

    const analysis = fetchedCreditAnalysis || creditAnalysisByAccount || creditAnalysis;
    console.log('[CREDIT ANALYSIS DEBUG] SELECTED analysis:', {
      id: analysis?.id,
      source: !fetchedCreditAnalysis ? 'fromAccount' : 'fromProposal',
      status: analysis?.status,
      proposalId: analysis?.proposalId,
      newAnalysisIn: analysis?.newAnalysisIn,
    });

    if (analysis) {
      setLocalCreditAnalysis(analysis);
      if (analysis.id !== creditAnalysis?.id) {
        console.log('[CREDIT ANALYSIS DEBUG] Updating context creditAnalysis');
        setCreditAnalysis(analysis);
      }
    } else if (creditAnalysis) {
      console.log('[CREDIT ANALYSIS DEBUG] Using context creditAnalysis');
      setLocalCreditAnalysis(creditAnalysis);
    } else {
      console.log('[CREDIT ANALYSIS DEBUG] NO ANALYSIS FOUND - setting to null');
      setLocalCreditAnalysis(null);
    }
  }, [
    fetchedCreditAnalysis,
    creditAnalysisByAccount,
    creditAnalysis?.id,
    setCreditAnalysis,
    creditAnalysis,
    proposal?.id,
    proposal?.creditAnalysisId,
  ]);

  const handleBack = () => {
    navigation.goToClient();
  };

  const handleNextStep = () => {
    const isApproved =
      status === CREDIT_ANALYSIS_STATUS.APPROVED ||
      status === CREDIT_ANALYSIS_STATUS.PARTIALLY_APPROVED;

    if (isApproved) {
      navigation.goToCart();
    }
  };

  const handleRequestNewAnalysis = () => {
    setShowNewAnalysisDialog(true);
  };

  const handleConfirmNewAnalysis = async () => {
    if (!account?.id || !proposal?.id) return;

    setIsCreatingAnalysis(true);
    try {
      const payload = {
        accountId: account.id,
        proposalId: proposal.id,
        status: CreditAnalysisStatus.PENDING_ANALYSIS,
      };

      const newAnalysis = await creditAnalysisApi.create(payload);

      // Update proposal in backend with the new creditAnalysisId
      await proposalApi.update(proposal.id, { creditAnalysisId: newAnalysis.id });

      setLocalCreditAnalysis(newAnalysis);
      setCreditAnalysis(newAnalysis);

      // Update proposal in local storage
      setProposal({
        ...proposal,
        creditAnalysisId: newAnalysis.id,
      });

      // Invalidate queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['creditAnalysis', 'account', account.id] });
      queryClient.invalidateQueries({ queryKey: ['creditAnalysis', 'proposal', proposal.id] });
      queryClient.invalidateQueries({ queryKey: ['proposal', proposal.id] });
      queryClient.invalidateQueries({ queryKey: ['credit-analysis'] });

      setShowNewAnalysisDialog(false);

      toast({
        title: 'Nova análise criada',
        description: 'Uma nova análise de crédito foi criada com sucesso.',
      });
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Erro ao criar nova análise de crédito.';
      toast({
        title: 'Erro',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsCreatingAnalysis(false);
    }
  };

  const handleDocumentRequestSuccess = (newCreditAnalysis: CreditAnalysis) => {
    setLocalCreditAnalysis(newCreditAnalysis);
    setCreditAnalysis(newCreditAnalysis);
  };

  const handleDocumentUploadSuccess = (updatedCreditAnalysis: CreditAnalysis) => {
    setLocalCreditAnalysis(updatedCreditAnalysis);
    setCreditAnalysis(updatedCreditAnalysis);
  };

  if (!proposal?.id) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Proposta não encontrada</AlertTitle>
          <AlertDescription>
            É necessário criar uma proposta antes de prosseguir com a análise de crédito.
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para seleção de cliente
        </Button>
      </div>
    );
  }

  // V-RN-01: Contact lock - Step 2 requires at least one contact selected
  if (contacts.length === 0 || selectedContactIds.size === 0) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Contatos não selecionados</AlertTitle>
          <AlertDescription>
            É necessário selecionar pelo menos um contato antes de prosseguir com a análise de
            crédito.
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para seleção de cliente
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* V-FE-01: Binary expiration state UI - "Em dia" message */}
      {!expirationValidation.isExpired &&
        (status === CREDIT_ANALYSIS_STATUS.APPROVED ||
          status === CREDIT_ANALYSIS_STATUS.PARTIALLY_APPROVED) && (
          <Alert variant="default" className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-700">Análise em dia</AlertTitle>
            <AlertDescription className="text-green-600">
              Não há análise pendente! A análise de crédito está válida.
            </AlertDescription>
          </Alert>
        )}

      {(status === CREDIT_ANALYSIS_STATUS.APPROVED ||
        status === CREDIT_ANALYSIS_STATUS.PARTIALLY_APPROVED ||
        status === CREDIT_ANALYSIS_STATUS.DECLINED) &&
        localCreditAnalysis && (
          <CreditAnalysisVerdictDetails
            creditAnalysis={localCreditAnalysis}
            onRequestNewAnalysis={handleRequestNewAnalysis}
            isCreatingNewAnalysis={isCreatingAnalysis}
          />
        )}

      {status === CREDIT_ANALYSIS_STATUS.DOC_DECLINED && (
        <>
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Documentos reprovados</AlertTitle>
            <AlertDescription>
              Alguns documentos foram reprovados. Por favor, envie novos documentos para continuar
              com a análise.
            </AlertDescription>
          </Alert>
          <Card>
            <CardHeader>
              <CardTitle>Reenviar Documentos</CardTitle>
            </CardHeader>
            <CardContent>
              <DocumentUploadForm
                proposalId={proposal.id}
                account={account || null}
                accountType={accountType}
                creditAnalysis={localCreditAnalysis}
                requiredDocuments={stateMachine.requiredDocuments.map((f) => f.key)}
                declinedDocuments={declinedDocuments}
                onSuccess={handleDocumentUploadSuccess}
              />
            </CardContent>
          </Card>
        </>
      )}

      {status === CREDIT_ANALYSIS_STATUS.EXPIRED && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Análise expirada</AlertTitle>
          <AlertDescription>
            Esta análise de crédito expirou. É necessário solicitar uma nova análise.
          </AlertDescription>
          <Button onClick={handleRequestNewAnalysis} className="mt-4" disabled={isCreatingAnalysis}>
            {isCreatingAnalysis ? 'Solicitando...' : 'Solicitar nova análise'}
          </Button>
        </Alert>
      )}

      {status === CREDIT_ANALYSIS_STATUS.NOT_STARTED && (
        <SendDocumentRequestForm
          proposalId={proposal.id}
          accountId={account?.id}
          account={account}
          contacts={filteredContacts}
          onSuccess={handleDocumentRequestSuccess}
        />
      )}

      {hasCreditAnalysis && status !== CREDIT_ANALYSIS_STATUS.NOT_STARTED && (
        <>
          <CardTitle>Histórico</CardTitle>
          <CreditAnalysisTimeline
            creditAnalysisId={localCreditAnalysis?.id || null}
            entries={timelineEntries}
            creditAnalysis={localCreditAnalysis}
          />
        </>
      )}

      {status === CREDIT_ANALYSIS_STATUS.PENDING_EXTERNAL_DOCS && (
        <div className="flex justify-end w-full">
          <Button onClick={() => setResendDialogOpen(true)}>Reenviar Solicitação</Button>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        {(status === CREDIT_ANALYSIS_STATUS.APPROVED ||
          status === CREDIT_ANALYSIS_STATUS.PARTIALLY_APPROVED) && (
          <Button onClick={handleNextStep}>
            Próxima etapa
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>

      <ResendDocumentRequestDialog
        open={resendDialogOpen}
        onClose={() => setResendDialogOpen(false)}
        proposalId={proposal.id}
        accountId={account?.id}
        contacts={contacts}
        recipients={allEmails}
        creditAnalysisId={localCreditAnalysis?.id || ''}
        onSuccess={handleDocumentRequestSuccess}
      />

      {/* V-FE-03: Confirmation Modal for New Analysis */}
      <NewAnalysisConfirmationDialog
        isOpen={showNewAnalysisDialog}
        onClose={() => setShowNewAnalysisDialog(false)}
        onConfirm={handleConfirmNewAnalysis}
        isPending={isCreatingAnalysis}
      />
    </div>
  );
}
